import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type { ClientProjectDto, ClientProjectInput, ClientProjectListItem } from './types';

/**
 * Fábrica do `CrudApi` aninhado em um Cliente. O `clientId` fica fechado no
 * escopo, então a página consumidora não precisa repassar a cada chamada.
 */
export function createClientProjectsApi(
  clientId: number,
): CrudApi<ClientProjectListItem, ClientProjectDto, ClientProjectInput> {
  const BASE = `/v1/clients/${clientId}/projects`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<ClientProjectListItem>> {
      const { data } = await api.get<{
        data: ClientProjectListItem[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params: toGenexusParams(query) });
      return normalizeListEnvelope(data);
    },

    async get(id: number): Promise<ClientProjectDto> {
      const { data } = await api.get<{ data: ClientProjectDto }>(`${BASE}/${id}`);
      return data.data;
    },

    async create(input: ClientProjectInput): Promise<ClientProjectDto> {
      const { data } = await api.post<{ data: ClientProjectDto }>(BASE, input);
      return data.data;
    },

    async update(id: number, input: ClientProjectInput): Promise<ClientProjectDto> {
      const { data } = await api.put<{ data: ClientProjectDto }>(`${BASE}/${id}`, input);
      return data.data;
    },

    async remove(id: number): Promise<void> {
      await api.delete(`${BASE}/${id}`);
    },
  };
}

/**
 * Hooks CRUD para projetos de um Cliente específico. Construídos em runtime
 * porque a `queryKey` precisa incluir `clientId` (cada cliente tem cache próprio).
 *
 * `createCrudHooks` não serve aqui porque chamaria `useQuery` fora de um
 * componente — fazemos o wiring manual com `useMemo` para o api e hooks vivem
 * dentro do consumer.
 */
export function useClientProjectsHooks(clientId: number): CrudHooks<
  ClientProjectListItem,
  ClientProjectDto,
  ClientProjectInput
> & {
  api: CrudApi<ClientProjectListItem, ClientProjectDto, ClientProjectInput>;
} {
  const projectsApi = useMemo(() => createClientProjectsApi(clientId), [clientId]);
  const queryKey = useMemo(() => ['clients', clientId, 'projects'] as const, [clientId]);
  const qc = useQueryClient();

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<ClientProjectListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query],
      queryFn: () => projectsApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (id: number | null | undefined): UseQueryResult<ClientProjectDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', id],
      queryFn: () => projectsApi.get(id as number),
      enabled: id !== null && id !== undefined,
    });

  const useCreate = (): UseMutationResult<ClientProjectDto, Error, ClientProjectInput> =>
    useMutation({
      mutationFn: (input) => projectsApi.create(input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useUpdate = (): UseMutationResult<
    ClientProjectDto,
    Error,
    { id: number; input: ClientProjectInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => projectsApi.update(id, input),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  const useDelete = (): UseMutationResult<void, Error, number> =>
    useMutation({
      mutationFn: (id) => projectsApi.remove(id),
      onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

  return { api: projectsApi, useList, useDetail, useCreate, useUpdate, useDelete };
}
